/**
 * DisplayNameSourceIndicator
 *
 * Shows a Chip badge for non-user sources (the display_name_source_indicator/
 * display_name_source_tooltip AB flags were promoted to permanent/removed
 * respectively — badge-on was the only variant ever shipped).
 *
 * Phase 1: sourceLabel is always undefined.
 * Phase 2: pass sourceLabel={row.manufacturer_name} when source is 'manufacturer'.
 */

import React from 'react';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import {
  DISPLAY_NAME_SOURCE,
  DisplayNameSource,
} from '../services/api/domains/products/types';

export interface DisplayNameSourceIndicatorProps {
  /**
   * The computed source of the display name. Widened beyond the 4-value
   * product `DisplayNameSource` union (issue #653, Sub-task 11.1) so
   * manufacturer callers can pass one of the finer-grained
   * `MANUFACTURER_NAME_ROLE_LABELS` role strings (e.g. `'gleif_name'`) —
   * unrecognized values fall back to `sourceLabel`/the raw string below,
   * they don't need a `SOURCE_CONFIG` entry.
   */
  displayNameSource: DisplayNameSource | string | null | undefined;
  /**
   * Optional label override for badge/tooltip text.
   * Phase 1: always undefined.
   * Phase 2: pass manufacturer_name when source is 'manufacturer'.
   */
  sourceLabel?: string;
  children?: React.ReactNode;
}

/** Per-source configuration */
const SOURCE_CONFIG: Record<
  string,
  {
    label: string;
    color: 'default' | 'primary' | 'secondary' | 'info' | 'warning';
    ariaLabel: string;
  }
> = {
  [DISPLAY_NAME_SOURCE.ORANGE_BOOK]: {
    // TODO(icons): replace with final assets — see Task 18
    label: 'ORANGE BOOK',
    color: 'warning',
    ariaLabel: 'Name sourced from FDA Orange Book',
  },
  [DISPLAY_NAME_SOURCE.CAS]: {
    // TODO(icons): replace with final assets — see Task 18
    label: 'CAS REGISTRY',
    color: 'default',
    ariaLabel: 'Name sourced from CAS registry',
  },
  [DISPLAY_NAME_SOURCE.USER]: {
    label: 'LGM Product Name',
    color: 'default',
    ariaLabel: 'LGM-assigned product name',
  },
  [DISPLAY_NAME_SOURCE.MANUFACTURER]: {
    // sourceLabel (manufacturer_name) overrides this label when available
    label: 'Manufacturer Catalogue',
    color: 'info',
    ariaLabel: 'Name sourced from manufacturer catalogue',
  },
};

const DisplayNameSourceIndicator: React.FC<DisplayNameSourceIndicatorProps> = ({
  displayNameSource,
  sourceLabel,
  children,
}) => {
  const isUserSource = displayNameSource === DISPLAY_NAME_SOURCE.USER;

  // Chip only renders for a real, non-user source.
  if (!displayNameSource || isUserSource) {
    return children ? <>{children}</> : null;
  }

  const config = SOURCE_CONFIG[displayNameSource];
  const badgeText = sourceLabel ?? config?.label ?? displayNameSource;
  const ariaLabelText = sourceLabel
    ? `Name sourced from ${sourceLabel}`
    : (config?.ariaLabel ?? `Name source: ${displayNameSource}`);

  return (
    <Box
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
      }}>
      <Box
        style={{
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
        {children}
      </Box>
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          flexShrink: 0,
          marginLeft: 'auto',
          paddingLeft: 4,
        }}>
        <Chip
          label={badgeText}
          size="small"
          color={config?.color ?? 'default'}
          aria-label={ariaLabelText}
          style={{
            height: 16,
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '0.04em',
            flexShrink: 0,
            pointerEvents: 'none',
          }}
        />
      </Box>
    </Box>
  );
};

export default DisplayNameSourceIndicator;
